import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import type { PluginListenerHandle } from "@capacitor/core";
import { isNative } from "../utils/tokens";
import { isRedirectUrlMatching } from "../utils/oauth";
import { CUSTOM_URL_SCHEME_HANDLER_TIMEOUT } from "../constants";

export class MobileNavigator {
  private capacitorAppUrlOpenHandle?: PluginListenerHandle;
  private capacitorBrowserFinishedHandle?: PluginListenerHandle;

  private resolve?: (value: string) => void;
  private reject?: (reason?: unknown) => void;

  private timer?: NodeJS.Timeout;
  private isClosed = true;
  private receivedResult = false;

  constructor(public redirectUrl: string) {}

  /**
   * Navigates to a URL and listens for a custom URL scheme to be opened. It
   * resolves the promise when the custom URL scheme is opened. It rejects the
   * promise when the browser is closed by the user.
   *
   * @see https://capacitorjs.com/docs/apis/app#appurlopen
   *
   * @param url - The URL to navigate to.
   * @returns The URL that was opened.
   */
  public async navigate(url: string): Promise<string> {
    this.isClosed = false;
    this.receivedResult = false;

    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;

      this.installCustomUrlSchemeHandler()
        .then(() => {
          this.useCapacitorBrowser(url);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public async cleanup(): Promise<void> {
    await this.capacitorAppUrlOpenHandle?.remove();
    await this.capacitorBrowserFinishedHandle?.remove();
    clearTimeout(this.timer);
  }

  public async close(): Promise<void> {
    if (!this.isClosed) {
      await Browser.close();
    }
  }

  private async installCustomUrlSchemeHandler(): Promise<void> {
    this.timer = setTimeout(
      () =>
        this.onError(
          "Installing custom url scheme handler timed out without a response",
        ),
      CUSTOM_URL_SCHEME_HANDLER_TIMEOUT,
    );

    await this.cleanup();

    if (isNative()) {
      this.capacitorAppUrlOpenHandle = await App.addListener(
        "appUrlOpen",
        ({ url }) => {
          if (isRedirectUrlMatching(url, this.redirectUrl)) {
            this.onSuccess(url);
          }
        },
      );
    }
  }

  private async onSuccess(url: string): Promise<void> {
    await this.close();
    await this.cleanup();
    this.receivedResult = true;
    this.resolve?.(url);
  }

  private async onError(message: string): Promise<void> {
    await this.close();
    await this.cleanup();
    this.reject?.(new Error(message));
    this.receivedResult = true;
  }

  private async useCapacitorBrowser(url: string): Promise<void> {
    this.capacitorBrowserFinishedHandle = await Browser.addListener(
      "browserFinished",
      () => {
        this.isClosed = true;

        setTimeout(() => {
          if (!this.receivedResult && this.reject) {
            this.cleanup();
            this.reject("Capacitor browser closed by user");
          }
        }, 1000);
      },
    );

    await Browser.open({ url });
  }
}
